import type { QueryClient } from '@tanstack/react-query';

/**
 * INV-13, live half: which cache entries a server notice touches.
 *
 * The SSE stream carries OBJECT METADATA IDS ONLY (INV-4) - never a record id, never a
 * value - so a notice cannot say "record X changed". It says "something in object X
 * changed", and this file turns that into the smallest honest set of cache operations:
 *
 *  - RECORD READ keys (`recordReadKeys` below: the list prefix AND the grouped prefix)
 *    are invalidated. A list holds an ordered array of ids and a grouped entry holds
 *    counts, and an insert, a delete or a sort-relevant edit can change either, so both
 *    have to come from the server again.
 *  - CANONICAL record keys (`['record', objectId, recordId]`) are marked stale but only
 *    refetched while MOUNTED (`refetchType: 'active'`). A tab that has 400 records in its
 *    cache and one on screen must issue one request, not 400.
 *  - TIMELINES follow the record they belong to, same rule.
 *  - NOTES, TASKS AND COMMENTS follow it too: the annotations module notifies with the
 *    parent's object id (notes-tasks-comments.md 3.1), so the granularity matches exactly.
 *  - SEARCH results are stale after any write; only a visible command menu refetches.
 *
 * A metadata notice does all of that AND the metadata queries, because a field id can
 * change meaning: the field list has to be right before the values are read again.
 *
 * The four notice functions stay PURE - a `QueryClient` is not needed to assert that an
 * invalidate notice touches a handful of key prefixes and not the whole cache. The one
 * function that does take a client, `invalidateRecordReads`, takes it as a parameter and
 * holds nothing.
 *
 * And one more pure function lives here for the same reason the key list does:
 * `cancelRefetchFor` decides whether an invalidation may cancel a fetch already in flight
 * (`table-completion.md` D25). It is a property of the KEY, so it belongs beside the keys.
 */

export interface CacheOperation {
  readonly queryKey: readonly unknown[];
  /**
   * `active` refetches only mounted queries and leaves the rest stale for their next
   * mount; `all` refetches everything under the key.
   */
  readonly refetch: 'active' | 'all';
}

/**
 * EVERY KEY PREFIX UNDER WHICH A READ OF RECORD DATA LIVES (group-by-aggregation.md D25).
 *
 * Two prefixes, not one, and that is the price of giving the grouped read its own
 * (`queryKeys.recordGroups`). `queryKeys.ts` says why prefix matching used to be free:
 * the paged list form kept the `recordList` prefix precisely so that "every invalidation
 * in the app (`['recordList', objectId]`) reaches both without knowing either exists". A
 * new prefix opts out of exactly that.
 *
 * So the fix is a mechanism rather than an enumeration: a new read prefix is added HERE,
 * once, and no call site can forget it. Obligation 41 asserts at the source level that no
 * other file in `packages/web/src` passes either literal to `invalidateQueries`.
 *
 * Omit the object id for the whole-app form, which is what a dropped-stream resync and an
 * unmerge need - neither can name the objects that moved.
 */
export function recordReadKeys(objectId?: string): readonly (readonly unknown[])[] {
  return objectId === undefined
    ? [['recordList'], ['recordGroups']]
    : [
        ['recordList', objectId],
        ['recordGroups', objectId],
      ];
}

/**
 * A RECORD READ THAT CAN OUTLAST THE COALESCING WINDOW MUST BE ALLOWED TO FINISH
 * (`table-completion.md` D25).
 *
 * `refetchQueries` defaults `cancelRefetch: true` (`query-core/queryClient.js`, and
 * `invalidateQueries` forwards its options to it), which cancels an in-flight fetch
 * SILENTLY when the entry already holds data (`query.js`: `cancel({ silent: true })`, so
 * nothing is thrown and nothing is stored). Two reads under this prefix take longer than
 * one 250 ms window: a paged entry's re-walk, which is N CHAINED round trips because each
 * page's cursor comes from the one before it, and a count-only entry's `count(*)`, which is
 * a full scan of the filter. Cancelled and restarted four times a second, neither ever
 * completes - the `Show more` button stops spinning with no row added and nothing said, and
 * the count never arrives at all.
 *
 * THE DECISION LIVES HERE, ONCE, and every caller takes its value from this predicate
 * rather than remembering the rule - which is `recordReadKeys`' own shape one function up.
 * Three call sites pass the flag: this file's `invalidateRecordReads`, the SSE applier
 * (`useLiveUpdates.ts`) and the optimistic reconciler (`optimisticList.ts`, which passes a
 * literal `false` because its loop only ever holds `recordList` keys). Obligation 38 names
 * all three at the source level, because a rule phrased as "no file outside the two the
 * cache suite exempts" would be broken by the applier, which lives in neither.
 *
 * THE WHOLE `recordList` PREFIX, which is wider than the paged form the defect was found
 * on. The narrower predicate was available - `queryKeys.recordListPages` puts the literal
 * `'pages'` in slot 2 - and is declined because the count entry has the same problem for a
 * different reason: a slow `count(*)` cancelled on every frame would leave the status row
 * with no denominator permanently, and nothing about that looks wrong enough to report.
 * The GROUPED prefix keeps the default deliberately: no user-triggered fetch runs on it,
 * and changing a grouped read's refresh semantics by analogy is an adjacent edit to raise
 * rather than to make.
 *
 * WHAT THE FRAME GIVES UP: with `cancelRefetch: false` a frame arriving while the entry is
 * already fetching DEDUPES onto that fetch instead of restarting it, so that frame's list
 * refresh is skipped for that one entry. Row VALUES are unaffected - every visible cell
 * resolves from a canonical record entry, which the same frame refetches - so what waits is
 * MEMBERSHIP and ORDER, until the next frame, a remount or a page-size change.
 */
export function cancelRefetchFor(queryKey: readonly unknown[]): boolean {
  return queryKey[0] !== 'recordList';
}

/**
 * The same key list, INVALIDATED (D25). For the mutation sites in `hooks.ts`, which call
 * the client directly rather than returning a descriptor.
 *
 * Two exports rather than one, because the eight sites are not the same kind of call: the
 * SSE paths return `CacheOperation[]` carrying their own refetch mode, and a single
 * `(queryClient, objectId?)` helper cannot serve a descriptor that decides for itself
 * whether unmounted queries refetch.
 *
 * IT TAKES `cancelRefetchFor`'S DECISION TOO, and that is not a symmetry for its own sake
 * (`table-completion.md` D25). This helper serves six mutation sites, two of which the flat
 * table itself mounts: a create from `RecordListPage` and a restore from
 * `ArchivedRowAction`, the second of which is deliberately not optimistic, so its round
 * trip is visible and the reader is free to press `Show more` inside it. On the default
 * that click's append was cancelled silently by the restore's own invalidation, with no SSE
 * frame and nobody else in the organization involved. The cost is the same trade with one
 * more door in it: a created row that belongs on page 1 can wait for a running append
 * before it appears, and its VALUES are already in the canonical entry the mutation wrote.
 */
export function invalidateRecordReads(queryClient: QueryClient, objectId?: string): void {
  for (const queryKey of recordReadKeys(objectId)) {
    void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: cancelRefetchFor(queryKey) });
  }
}

/** Record DATA in these objects changed. */
export function recordInvalidations(objectIds: readonly string[]): readonly CacheOperation[] {
  const operations: CacheOperation[] = [];
  for (const objectId of new Set(objectIds)) {
    operations.push(
      // A list is an id array and a grouped entry is a set of counts: both must be
      // refetched even when they are not on screen, or a deleted record is still there
      // when the user comes back to the tab. `all` is the mode, and it is the difference
      // from the resync below that must survive any refactor of these two functions.
      ...recordReadKeys(objectId).map<CacheOperation>((queryKey) => ({
        queryKey,
        refetch: 'all',
      })),
      { queryKey: ['record', objectId], refetch: 'active' },
      { queryKey: ['recordEvents', objectId], refetch: 'active' },
      /**
       * ANNOTATIONS (notes-tasks-comments.md 3.5). The module calls
       * `notifyRecordsChanged` with the parent's object id after every write, so this
       * notice is the ONLY thing that tells an open record page that a colleague just
       * commented. Same `active` rule as the record keys: the notice names an object, and
       * every note in that object's cache must not become a request because one moved.
       */
      { queryKey: ['notes', objectId], refetch: 'active' },
      { queryKey: ['tasks', objectId], refetch: 'active' },
      { queryKey: ['comments', objectId], refetch: 'active' },
    );
  }
  if (operations.length > 0) {
    operations.push(
      { queryKey: ['search'], refetch: 'active' },
      /**
       * The my-tasks feed and its badge are refetched even UNMOUNTED, following the
       * `pendingCount` precedent and for the same reason: the badge is in the nav on every
       * screen, and being assigned a task is exactly the thing a person must not learn
       * about only by navigating somewhere. Neither key carries an object id, so the
       * per-object loop above cannot reach them.
       */
      { queryKey: ['myTasks'], refetch: 'all' },
      { queryKey: ['myTaskCount'], refetch: 'all' },
    );
  }
  return operations;
}

/**
 * Pending changes were created or decided (INV-10).
 *
 * The notice carries pending-change IDS ONLY, and they are not cache keys here: the
 * queue is one list plus one count, and refetching both is cheaper and simpler than
 * patching individual entries from a notice that deliberately contains no data. The
 * COUNT is refetched even when nothing is mounted (`all`), because the badge lives in the
 * nav on every screen and a stale badge is the one thing this notice exists to fix.
 */
export function pendingInvalidations(): readonly CacheOperation[] {
  return [
    { queryKey: ['pendingCount'], refetch: 'all' },
    { queryKey: ['pendingChanges'], refetch: 'active' },
    { queryKey: ['pendingChange'], refetch: 'active' },
  ];
}

/** The metadata VERSION moved; these objects were touched. */
export function metadataInvalidations(objectIds: readonly string[]): readonly CacheOperation[] {
  const operations: CacheOperation[] = [{ queryKey: ['objects'], refetch: 'all' }];
  for (const objectId of new Set(objectIds)) {
    operations.push(
      { queryKey: ['object', objectId], refetch: 'all' },
      { queryKey: ['fields', objectId], refetch: 'all' },
    );
  }
  // Field ids feed `select`, so a model change invalidates the reads built from it.
  return [...operations, ...recordInvalidations(objectIds)];
}

/**
 * Everything, for a stream that was DOWN.
 *
 * A client that missed notices cannot know which objects moved, and the alternative to a
 * blunt resync is a UI that is quietly wrong until something else happens to refetch. The
 * `active` scope keeps the cost at "what is on screen".
 */
export function resyncInvalidations(): readonly CacheOperation[] {
  return [
    { queryKey: ['objects'], refetch: 'active' },
    { queryKey: ['object'], refetch: 'active' },
    { queryKey: ['fields'], refetch: 'active' },
    /**
     * Both record read prefixes, at `active` - which is the scope difference from
     * `recordInvalidations`' `all` and is deliberate, not an oversight the shared key list
     * flattens: a resync cannot name what moved, so it pays for what is on screen.
     */
    ...recordReadKeys().map<CacheOperation>((queryKey) => ({ queryKey, refetch: 'active' })),
    { queryKey: ['record'], refetch: 'active' },
    { queryKey: ['recordEvents'], refetch: 'active' },
    { queryKey: ['notes'], refetch: 'active' },
    { queryKey: ['tasks'], refetch: 'active' },
    { queryKey: ['comments'], refetch: 'active' },
    { queryKey: ['myTasks'], refetch: 'active' },
    /**
     * The two BADGE counts are what a resync refetches unmounted, because they are on
     * screen whatever the user is looking at. A stale pending count and a stale task
     * count are the two ways a dropped stream stays invisible after it recovers.
     */
    { queryKey: ['pendingCount'], refetch: 'all' },
    { queryKey: ['myTaskCount'], refetch: 'all' },
    { queryKey: ['pendingChanges'], refetch: 'active' },
  ];
}
